import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, GraduationCap, LoaderCircle, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { Input } from '../components/ui/input'
import { Button, buttonVariants } from '../components/ui/button'
import { cn } from '../lib/utils'
import {
  STUDENT_MASCOTS,
  STUDENT_SECRET_EMOJIS,
  buildStudentPassword,
  buildStudentUsername,
  normalizeStudentFullName,
  submitRoboLabLogin,
} from '../lib/robolabx-login-catalog'
import { RobotLogo } from '../components/RobotLogo'

type StudentLoginStep = 'name' | 'mascot' | 'secret'

export const STUDENT_LOGIN_TITLE = 'Welcome back'
const STEP_CONTENT_FRAME_PADDING = 8

function LoginShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-start justify-center overflow-y-auto bg-background px-4 py-4 text-foreground sm:items-center">
      <div className="w-full max-w-[520px] rounded-base border-2 border-nb-border bg-secondary-bg p-5 nb-shadow">
        {children}
      </div>
    </div>
  )
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" variant="neutral" onClick={onClick}>
      <ArrowLeft className="size-4" />
      Back
    </Button>
  )
}

function AdminLoginLink() {
  return (
    <a
      href="/admin/login"
      className={cn(
        buttonVariants({ variant: 'ghost' }),
        'text-sm',
      )}
    >
      Admin login
    </a>
  )
}

export function StudentLoginPage() {
  const [step, setStep] = useState<StudentLoginStep>('name')
  const [fullName, setFullName] = useState('')
  const [selectedMascotId, setSelectedMascotId] = useState<string | null>(null)
  const [selectedSecretIds, setSelectedSecretIds] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const stepContentRef = useRef<HTMLDivElement | null>(null)
  const [stepContentHeight, setStepContentHeight] = useState<number | null>(null)

  const normalizedFullName = useMemo(() => normalizeStudentFullName(fullName), [fullName])
  const selectedMascot = useMemo(
    () => STUDENT_MASCOTS.find((mascot) => mascot.id === selectedMascotId) ?? null,
    [selectedMascotId],
  )

  function moveToNameStep() {
    if (isSubmitting) return
    setStep('name')
    setSelectedMascotId(null)
    setSelectedSecretIds([])
  }

  function moveToMascotStep() {
    if (isSubmitting) return
    setStep('mascot')
    setSelectedSecretIds([])
  }

  function handleContinueName() {
    if (!normalizedFullName) return
    setStep('mascot')
  }

  function handleSelectMascot(mascotId: string) {
    if (isSubmitting) return
    setSelectedMascotId(mascotId)
    setSelectedSecretIds([])
    setStep('secret')
  }

  async function submitStudentSecret(secretIds: string[]) {
    if (!selectedMascotId) return

    setIsSubmitting(true)

    try {
      await submitRoboLabLogin({
        username: buildStudentUsername(selectedMascotId, normalizedFullName),
        password: buildStudentPassword(secretIds),
      })
      window.location.replace('/')
    } catch {
      setSelectedSecretIds([])
      toast.error('Unable to sign in. Please check your details and try again.')
      setIsSubmitting(false)
    }
  }

  async function handleSecretTap(secretId: string) {
    if (isSubmitting) return

    const next = [...selectedSecretIds, secretId].slice(0, 3)
    setSelectedSecretIds(next)

    if (next.length === 3) {
      await submitStudentSecret(next)
    }
  }

  function handleUndoTap() {
    if (isSubmitting || selectedSecretIds.length === 0) return
    setSelectedSecretIds((current) => current.slice(0, -1))
  }

  useLayoutEffect(() => {
    const node = stepContentRef.current
    if (!node) return

    const updateHeight = () => {
      setStepContentHeight(node.getBoundingClientRect().height)
    }

    updateHeight()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => updateHeight())
    observer.observe(node)
    return () => observer.disconnect()
  }, [step, fullName, selectedMascotId, selectedSecretIds.length, isSubmitting])

  return (
    <LoginShell>
      <div className="px-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RobotLogo className="h-12 w-12" />
            <div className="inline-flex items-center gap-2 rounded-full border-2 border-nb-border bg-background px-4 py-1 text-sm font-heading text-foreground/75">
              <GraduationCap className="size-4" />
              Student sign in
            </div>
          </div>
          {step === 'name' ? <AdminLoginLink /> : null}
          {step === 'mascot' ? <BackButton onClick={moveToNameStep} /> : null}
          {step === 'secret' ? <BackButton onClick={moveToMascotStep} /> : null}
        </div>

        <h1 className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1 font-heading text-[2rem] leading-none">
          {step === 'secret' && selectedMascot && normalizedFullName ? (
            <>
              <span>{`${STUDENT_LOGIN_TITLE},`}</span>
              <span className="inline-flex items-center gap-2">
                <span>{normalizedFullName}</span>
                <span>{selectedMascot.emoji}</span>
              </span>
            </>
          ) : (
            <span>{STUDENT_LOGIN_TITLE}</span>
          )}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-foreground/70">
          {step === 'secret'
            ? 'Tap your emoji secret.'
            : step === 'mascot'
              ? 'Tap the mascot that belongs to you.'
              : 'First type your full name. Then use your mascot and emoji secret.'}
        </p>
      </div>

      <div
        className="mt-5 max-h-[calc(100vh-15rem)] overflow-y-auto px-2 py-2 pr-1 transition-[height] duration-300 ease-out"
        style={stepContentHeight ? { height: `${stepContentHeight + STEP_CONTENT_FRAME_PADDING * 2}px` } : undefined}
      >
        <div
          key={step}
          ref={stepContentRef}
          className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
        >
          {step === 'name' ? (
            <div className="max-w-[460px]">
              <label className="text-[1.2rem] font-heading" htmlFor="student-full-name">
                Type your full name
              </label>
              <Input
                id="student-full-name"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                placeholder="For example: Mia Chen"
                className="mt-3 h-11 text-sm"
              />
              <p className="mt-3 text-sm leading-relaxed text-foreground/65">
                Use your whole name. Uppercase or lowercase are both okay.
              </p>
              <Button type="button" className="mt-5 text-sm" onClick={handleContinueName} disabled={!normalizedFullName}>
                Continue
              </Button>
            </div>
          ) : null}

          {step === 'mascot' ? (
            <div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {STUDENT_MASCOTS.map((mascot) => (
                  <button
                    key={mascot.id}
                    type="button"
                    onClick={() => handleSelectMascot(mascot.id)}
                    className="h-[110px] w-full max-w-[110px] rounded-base border-2 border-nb-border bg-background p-3 text-left nb-shadow transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none"
                  >
                    <div className="flex h-full flex-col justify-between">
                      <div className="text-[1.75rem] leading-none">{mascot.emoji}</div>
                      <div className="font-heading text-[0.92rem] leading-none">{mascot.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {step === 'secret' ? (
            <div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {STUDENT_SECRET_EMOJIS.map((secret) => (
                  <button
                    key={secret.id}
                    type="button"
                    onClick={() => void handleSecretTap(secret.id)}
                    disabled={isSubmitting}
                    className="h-[110px] w-full max-w-[110px] rounded-base border-2 border-nb-border bg-background p-2.5 text-left nb-shadow transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none disabled:opacity-50"
                  >
                    <div className="flex h-full flex-col justify-between">
                      <div className="text-[1.6rem] leading-none">{secret.emoji}</div>
                      <div className="font-heading text-[0.85rem] leading-none">{secret.label}</div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-5 flex items-center justify-between gap-3">
                <div className="flex gap-2">
                  {Array.from({ length: 3 }).map((_, index) => {
                    const secret = STUDENT_SECRET_EMOJIS.find((item) => item.id === selectedSecretIds[index])
                    return (
                      <div
                        key={index}
                        className="flex h-12 w-12 items-center justify-center rounded-base border-2 border-nb-border bg-secondary-bg text-2xl"
                      >
                        {secret?.emoji ?? <span className="text-lg text-foreground/30">•</span>}
                      </div>
                    )
                  })}
                </div>

                {isSubmitting ? (
                  <div
                    aria-live="polite"
                    className="inline-flex items-center gap-2 text-sm font-heading text-foreground/70"
                  >
                    <LoaderCircle className="size-4 animate-spin text-foreground/45" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    aria-label="Undo last tap"
                    onClick={handleUndoTap}
                    disabled={selectedSecretIds.length === 0}
                    className={cn(buttonVariants({ variant: 'neutral', size: 'icon' }), 'shrink-0')}
                  >
                    <RotateCcw className="size-4" />
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </LoginShell>
  )
}
